import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, addMonths, subMonths, isSameDay, isSameMonth, isBefore, isToday } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';
import { X, Video, Check } from 'lucide-react';

export default function NewAppointmentModal({ onClose, onSave, types = [] }) {
  const { currentOrg } = useOrg();
  const { apiLocationFilter } = useLocations();
  const [form, setForm] = useState({
    type: types[0]?.key || '', clientId: '', staffId: '',
    date: '', startTime: '', endTime: '', location: '', notes: '',
  });
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [partners, setPartners] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [showCollabSection, setShowCollabSection] = useState(false);
  const [collabForm, setCollabForm] = useState({ orgId: '', userId: '', roleLabel: '' });
  const [partnerStaff, setPartnerStaff] = useState([]);
  const [loadingPartnerStaff, setLoadingPartnerStaff] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [videoVisit, setVideoVisit] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    Promise.all([
      api.getContacts(currentOrg.id),
      api.getStaff(currentOrg.id),
      api.getActivePartners(currentOrg.id),
    ]).then(([c, s, p]) => {
      setClients(c);
      setStaff(s);
      setPartners(p);
      setLoadingData(false);
    }).catch(() => setLoadingData(false));
  }, [currentOrg]);

  // Fetch partner staff when org is selected
  useEffect(() => {
    if (!collabForm.orgId || !currentOrg) {
      setPartnerStaff([]);
      return;
    }
    setLoadingPartnerStaff(true);
    api.getPartnerStaff(currentOrg.id, collabForm.orgId)
      .then(data => setPartnerStaff(data))
      .catch(() => setPartnerStaff([]))
      .finally(() => setLoadingPartnerStaff(false));
  }, [collabForm.orgId, currentOrg]);

  const addCollaborator = () => {
    if (!collabForm.orgId) return;
    const partner = partners.find(p => p.partner_org.id === collabForm.orgId);
    const staffMember = partnerStaff.find(s => s.user_id === collabForm.userId);
    setCollaborators(prev => [...prev, {
      org_id: collabForm.orgId,
      org_name: partner?.partner_org?.name || '',
      user_id: collabForm.userId || null,
      user_name: staffMember ? `${staffMember.user.first_name} ${staffMember.user.last_name}` : null,
      role_label: collabForm.roleLabel || null,
    }]);
    setCollabForm({ orgId: '', userId: '', roleLabel: '' });
  };

  const removeCollaborator = (index) => {
    setCollaborators(prev => prev.filter((_, i) => i !== index));
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Fetch available slots when provider + date selected
  useEffect(() => {
    if (!selectedDate || !currentOrg) { setSlots([]); return; }
    setSlotsLoading(true);
    setSelectedSlot(null);

    if (form.staffId) {
      // Fetch from API if provider selected
      api.getAvailability(currentOrg.id, {
        provider_id: form.staffId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        duration: 30,
        tz_offset: new Date().getTimezoneOffset(),
        ...(apiLocationFilter ? { location_id: apiLocationFilter } : {}), // the provider's hours at THIS office
      })
        .then(data => setSlots(data.filter(s => s.available)))
        .catch(() => setSlots([]))
        .finally(() => setSlotsLoading(false));
    } else {
      // No provider — show all slots 8am-6pm
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const generated = [];
      const dayStart = new Date(`${dateStr}T08:00:00`);
      const dayEnd = new Date(`${dateStr}T18:00:00`);
      const now = new Date();
      for (let t = new Date(dayStart); t < dayEnd; t = new Date(t.getTime() + 30 * 60000)) {
        if (t > now) generated.push({ starts_at: t.toISOString(), ends_at: new Date(t.getTime() + 30 * 60000).toISOString(), available: true });
      }
      setSlots(generated);
      setSlotsLoading(false);
    }
  }, [selectedDate, form.staffId, currentOrg, apiLocationFilter]);

  // Sync selected slot back to form
  useEffect(() => {
    if (selectedSlot) {
      const start = new Date(selectedSlot.starts_at);
      const end = new Date(selectedSlot.ends_at);
      update('date', format(start, 'yyyy-MM-dd'));
      update('startTime', format(start, 'HH:mm'));
      update('endTime', format(end, 'HH:mm'));
    }
  }, [selectedSlot]); // eslint-disable-line

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      collaborators,
      videoVisit,
      // Pass ISO strings directly if a slot was selected (avoids timezone reconstruction bugs)
      _starts_at: selectedSlot?.starts_at || null,
      _ends_at: selectedSlot?.ends_at || null,
    });
    onClose();
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex items-center justify-center z-[200] animate-fade-in p-4" onClick={onClose}>
      <div
        className="w-full max-w-[580px] max-h-[90vh] bg-primary rounded-xl border border-theme shadow-2xl overflow-y-auto animate-modal-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-7 py-5 border-b border-theme">
          <h3 className="text-[18px] font-bold tracking-tight">New Appointment</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all"
          >
            <X size={14} strokeWidth={2.2} />
          </button>
        </div>

        {/* Form */}
        <form className="px-6 md:px-7 py-6 space-y-5" onSubmit={handleSubmit}>
          {/* Type */}
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Appointment Type</label>
            <div className="flex flex-wrap gap-1.5">
              {types.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => update('type', t.key)}
                  className={`flex items-center gap-[7px] px-3.5 py-[7px] rounded-full text-[12px] font-semibold transition-all border
                    ${form.type === t.key
                      ? 'text-white shadow-md border-transparent'
                      : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface'}`}
                  style={form.type === t.key ? { background: t.color, borderColor: t.color } : undefined}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: form.type === t.key ? 'white' : t.color }}
                  />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact + Provider */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Contact</label>
              <select className={inputClass} value={form.clientId} onChange={e => update('clientId', e.target.value)} disabled={loadingData}>
                <option value="">{loadingData ? 'Loading...' : 'Select contact...'}</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Provider</label>
              <select className={inputClass} value={form.staffId} onChange={e => update('staffId', e.target.value)} disabled={loadingData}>
                <option value="">{loadingData ? 'Loading...' : 'Select provider...'}</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.first_name} {s.last_name} — {s.role?.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date + Time — Calendar with availability */}
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Date & Time</label>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
              {/* Calendar */}
              <div className="bg-tertiary rounded-[10px] p-3 border border-strong">
                <div className="flex items-center justify-between mb-2">
                  <button type="button" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))} className="w-7 h-7 rounded-lg bg-elevated text-muted-c flex items-center justify-center hover:bg-hover-surface text-[12px]">&larr;</button>
                  <span className="text-[12px] font-semibold text-primary-c">{format(calendarMonth, 'MMMM yyyy')}</span>
                  <button type="button" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))} className="w-7 h-7 rounded-lg bg-elevated text-muted-c flex items-center justify-center hover:bg-hover-surface text-[12px]">&rarr;</button>
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                    <div key={d} className="text-center text-[10px] font-semibold text-muted-c py-1">{d}</div>
                  ))}
                  {(() => {
                    const monthStart = startOfMonth(calendarMonth);
                    const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
                    return Array.from({ length: 42 }, (_, i) => addDays(calStart, i)).map((day, i) => {
                      const inMonth = isSameMonth(day, calendarMonth);
                      const isPast = isBefore(day, new Date()) && !isToday(day);
                      const isSel = selectedDate && isSameDay(day, selectedDate);
                      const isTod = isToday(day);
                      const disabled = isPast || !inMonth;
                      return (
                        <button
                          key={i} type="button"
                          onClick={() => !disabled && setSelectedDate(day)}
                          disabled={disabled}
                          className={`aspect-square flex items-center justify-center rounded-lg text-[11px] font-medium transition-all ${
                            isSel ? 'bg-accent text-white font-bold shadow-glow' :
                            isTod && !isSel ? 'bg-accent-glow text-accent-light font-bold' :
                            disabled ? 'text-muted-c/30' :
                            'text-primary-c hover:bg-hover-surface'
                          }`}
                        >{day.getDate()}</button>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Time slots */}
              <div>
                {selectedDate ? (
                  <div className="bg-tertiary rounded-[10px] p-3 border border-strong h-full flex flex-col">
                    <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2">{format(selectedDate, 'EEE, MMM d')}</div>
                    {slotsLoading ? (
                      <div className="space-y-1.5 flex-1">
                        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-8 bg-hover-surface rounded-lg animate-pulse" />)}
                      </div>
                    ) : slots.length === 0 ? (
                      <p className="text-[11px] text-muted-c text-center py-4 flex-1 flex items-center justify-center">No available times</p>
                    ) : (
                      <div className="space-y-1 flex-1 overflow-y-auto max-h-[200px]">
                        {slots.map(slot => {
                          const isSel = selectedSlot?.starts_at === slot.starts_at;
                          return (
                            <button
                              key={slot.starts_at} type="button"
                              onClick={() => setSelectedSlot(slot)}
                              className={`w-full py-2 rounded-lg text-[12px] font-semibold transition-all border ${
                                isSel ? 'text-white border-transparent shadow-glow' : 'bg-elevated text-secondary-c border-strong hover:bg-hover-surface'
                              }`}
                              style={isSel ? { background: '#0DA7CA' } : undefined}
                            >
                              {format(new Date(slot.starts_at), 'h:mm a')}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-tertiary rounded-[10px] p-3 border border-strong h-full flex items-center justify-center">
                    <p className="text-[11px] text-muted-c text-center">Select a date</p>
                  </div>
                )}
              </div>
            </div>
            {selectedSlot && (
              <div className="mt-2 text-[12px] font-medium text-accent-light">
                Selected: {format(new Date(selectedSlot.starts_at), 'EEEE, MMM d')} at {format(new Date(selectedSlot.starts_at), 'h:mm a')}
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Location</label>
            <input type="text" className={inputClass} placeholder="e.g., Office — Room 201" value={form.location} onChange={e => update('location', e.target.value)} />
          </div>

          {/* Video visit — books the telehealth room with the appointment
              so the patient's join link goes out immediately and the visit
              shows in Video Visits → Today, ready to join. */}
          <button
            type="button"
            onClick={() => setVideoVisit((v) => !v)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-[10px] border text-left transition-all ${videoVisit
              ? 'bg-accent-glow border-strong'
              : 'bg-tertiary border-theme hover:bg-hover-surface'}`}
          >
            <span className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${videoVisit ? 'gradient-accent text-white' : 'bg-elevated text-muted-c border border-theme'}`}>
              <Video size={16} />
            </span>
            <span className="flex-1">
              <span className="block text-[13px] font-semibold text-primary-c">Video visit</span>
              <span className="block text-[11.5px] text-muted-c">
                The patient gets a secure join link by email &amp; text — no download needed.
              </span>
            </span>
            <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${videoVisit ? 'gradient-accent border-transparent text-white' : 'border-strong'}`}>
              {videoVisit && <Check size={12} strokeWidth={3} />}
            </span>
          </button>

          {/* Notes */}
          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Notes</label>
            <textarea className={inputClass} rows={3} placeholder="Appointment notes..." value={form.notes} onChange={e => update('notes', e.target.value)} />
          </div>

          {/* Network Collaborators */}
          {partners.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowCollabSection(!showCollabSection)}
                className="flex items-center gap-2 text-[12px] font-semibold text-accent-light hover:text-primary-c transition-colors"
              >
                <span className={`transition-transform ${showCollabSection ? 'rotate-90' : ''}`}>&#9654;</span>
                Add Network Collaborator
                {collaborators.length > 0 && (
                  <span className="text-[10px] font-bold bg-accent-glow text-accent-light px-2 py-0.5 rounded-full">{collaborators.length}</span>
                )}
              </button>

              {showCollabSection && (
                <div className="mt-3 p-4 bg-tertiary rounded-[10px] border border-theme space-y-3">
                  {/* Added collaborators */}
                  {collaborators.length > 0 && (
                    <div className="space-y-1.5 mb-3">
                      {collaborators.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-2.5 bg-elevated rounded-lg border border-theme">
                          <div className="min-w-0">
                            <span className="text-[12px] font-semibold text-primary-c">{c.org_name}</span>
                            {c.user_name && <span className="text-[11px] text-muted-c ml-1.5">— {c.user_name}</span>}
                            {c.role_label && <span className="text-[10px] text-accent-light ml-1.5">({c.role_label})</span>}
                          </div>
                          <button type="button" onClick={() => removeCollaborator(i)} className="text-muted-c hover:text-danger text-[11px] ml-2">✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Collaborator form */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <select
                      className={inputClass}
                      value={collabForm.orgId}
                      onChange={e => setCollabForm(prev => ({ ...prev, orgId: e.target.value, userId: '' }))}
                    >
                      <option value="">Partner org...</option>
                      {partners.map(p => (
                        <option key={p.partner_org.id} value={p.partner_org.id}>{p.partner_org.name}</option>
                      ))}
                    </select>
                    <select
                      className={inputClass}
                      value={collabForm.userId}
                      onChange={e => setCollabForm(prev => ({ ...prev, userId: e.target.value }))}
                      disabled={!collabForm.orgId || loadingPartnerStaff}
                    >
                      <option value="">{loadingPartnerStaff ? 'Loading...' : 'Staff (optional)...'}</option>
                      {partnerStaff.map(s => (
                        <option key={s.user_id} value={s.user_id}>{s.user.first_name} {s.user.last_name}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Role (e.g., Treating Dr.)"
                      value={collabForm.roleLabel}
                      onChange={e => setCollabForm(prev => ({ ...prev, roleLabel: e.target.value }))}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addCollaborator}
                    disabled={!collabForm.orgId}
                    className="px-4 py-[7px] rounded-lg text-[11px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-40"
                  >
                    + Add
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2.5 pt-5 border-t border-theme">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all">
              Create Appointment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
